"use client";

import {
  Attachment,
  attachmentSchema,
  useDeleteAttachment,
  useDeleteHdAttachment,
  useGetAttachmentsByDocId,
  useUpdateAttachment,
} from "@/api/attachment";
import { uploadImage } from "@/api/image.ts";
import StyledDrawer from "@/components/StyledDrawer.tsx";
import { Button } from "@/components/ui/button.tsx";
import { FormControl, FormField, FormItem } from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabsContained.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import {
  isMainPosition,
  optionsPosition,
  TypeOptionPosition,
} from "@/lib/image";
import { zodResolver } from "@hookform/resolvers/zod";
import { Cross1Icon, PlusIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { CheckCircleIcon, Crown, Edit2, LayoutGrid, Table2, Trash2, XCircleIcon, ChevronsLeft } from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { UniqueImageFullScreen } from "../modal/UniqueImageFullScreen";
import { DataTable } from "../ui/datatable";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Separator } from "../ui/separator";
import { useEmbedAttachment } from "@/api/artmatch";
import DragAndDropLoadImage from "../form-fields/DragAndDropLoadImage";
import HdOption from "../hd/HdOption";
import ShowOptionsDialog from "../hd/ShowOptionsDialog";
import { Forfait } from "@/lib/types";
import { useGetForfait } from "@/api/forfait";
import { FileItemDisplay } from "../ui/file-item-display";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "../ui/dialog";
import { isAxiosError } from "axios";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import { MultilingualField } from "../form-items/MultilingualField";
import { Col } from "../form-items/AbstractForm";
import { getMainLanguage } from "@/i18n";
const imageSchema = attachmentSchema
  .pick({
    name: true,
    textAlt: true,
    caption: true,
    position: true,
    order: true,
    published: true,
    isHd: true,
  })
  .extend({
    image: z.instanceof(File).optional(),
  });

export type ImageFormData = z.infer<typeof imageSchema>;

interface UploadOrEditPictureProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedImage: Attachment | null;
  setSelectedImage: (image: Attachment | null) => void;
  onSubmit: (data: ImageFormData) => Promise<void>;
  onDelete?: (id: string) => void;
  type: TypeOptionPosition;
  form: UseFormReturn<ImageFormData>;
  inputName?: boolean;
}

export const UploadOrEditPicture = ({
                                      open,
                                      setOpen,
                                      selectedImage,
                                      setSelectedImage,
                                      onSubmit,
                                      onDelete,
                                      type,
                                      form,
                                      inputName,
                                    }: UploadOrEditPictureProps) => {
  const { t } = useTranslation();
  const { data: forfait } = useGetForfait();
  const { handleSubmit, control } = form;
  const [hdOptionSelected, setHdOptionSelected] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setHdOptionSelected(selectedImage ? selectedImage.isHd ?? true : true);
  }, [selectedImage]);

  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openConfirmationDelete, setOpenConfirmationDelete] = useState(false);
  const [selectedOptionName, setSelectedOptionName] = useState<string>("");
  const hasHdOption = useMemo(() =>
    forfait?.subscribedOptions?.find((option) => option.type === "HD") !== undefined,
    [forfait]
  );

  form.watch(["order", "published"]); // order and publish react to the position, so we need to watch them in order to rerender

  function onFacePositionTriggered() {
    form.setValue("order", 1, { shouldDirty: true, shouldTouch: true });
    form.trigger("order");
    form.setValue("published", true);
  }

  function subscribeToOptionSuccess(name: string) {
    setSelectedOptionName(name);
    setOpenConfirmation(true);
  }

  return (
    <StyledDrawer
      className="min-w-[500px]"
      title={
        selectedImage
          ? t("updateImage")
          : t("addNewImage")
      }
      sheetProps={{
        open,
        onOpenChange: () => {
          form.reset({
            name: "",
            textAlt: {},
            caption: {},
            position: "",
            published: false,
          });
          setOpen(!open);
          setSelectedImage(null);
        },
      }}
      trigger={
        <Button
          className="font-semibold flex gap-2"
          variant="outline"
          onClick={() => {
            form.reset({
              name: "",
              textAlt: {},
              caption: {},
              position: "",
              published: false,
            });
            setOpen(!open);
            setSelectedImage(null);
          }}
        >
          <PlusIcon className="h-4 w-4 relative" />
          {selectedImage ? t("modifierImageBouton") : t("ajouterImageBouton")}
        </Button>
      }
      closeButton={
        selectedImage ? (
          <Button
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (selectedImage && onDelete != undefined) {
                setOpenConfirmationDelete(true);
              }
            }}
          >
            <Trash2 className="w-5 h-5 text-[#DC2626]" />
          </Button>
        ) : (
          <Cross1Icon className="h-5 w-5 relative" onClick={() => {
            form.reset();
            setHdOptionSelected(true);
          }} />
        )
      }
      arrowClose={selectedImage !== null}
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit((data) => {
            if ((!hdOptionSelected && !selectedImage) || (!hdOptionSelected && selectedImage && selectedImage.isHd)){
              setShowWarningDialog(true);
              return;
            }
            data.isHd = hdOptionSelected && hasHdOption;
            setLoading(true);
            onSubmit(data).then(() => {
              setLoading(false);
            })
          }, (errors) => {
            console.log(errors);
            setLoading(false);
          })}
          className="w-full flex flex-col gap-4"
        >
          <div>
            {selectedImage && (
              <img
                src={selectedImage.mediumPublicUrl}
                alt="Selected"
                className="w-full h-[300px] object-contain"
              />
            )}
            {!selectedImage && (
                <DragAndDropLoadImage
                  control={control}
                  config={{ name: "image", type: "inputFile" }}
                  className="bg-white border border-solid z-5 relative"
                  content={t("acceptedImageTypes")}
                  getValues={form.getValues}
                  fileItemValidationMark={true}
                />
            )}
            {selectedImage && (
              <FileItemDisplay
                file={selectedImage}
                className="z-5 relative"
              />
            )}
            {(!selectedImage || selectedImage?.isHd) && (
                <HdOption
                  disabled={!selectedImage && hasHdOption && (form.getValues("image") === undefined || form.getValues("image") === null)}
                  isHdOptionEnabled={selectedImage ? selectedImage.isHd : hasHdOption}
                  hdOptionSelected={hdOptionSelected}
                  onHdOptionChange={(checked) => {
                    setHdOptionSelected(checked as boolean);
                  }}
                  setShowDialog={setShowDialog}
                />
            )}
          </div>
          {inputName && (
            <FormField
              control={control}
              name={"name"}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      label={t("attachment.form.name")}
                      id={field.name}
                      type="text"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
          {/*<HdOption*/}
          {/*  hdOptionEnabled={forfait?.hdOption ?? false}*/}
          {/*  hdOptionSelected={hdSelected}*/}
          {/*  onHdOptionChange={setHdSelected}*/}
          {/*  className="z-20"*/}
          {/*/>*/}
          <FormField
            control={control}
            name={"name"}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    label={t("attachment.form.name")}
                    id={field.name}
                    type="text"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <MultilingualField
            defaultLang={getMainLanguage()}
            render={(lang) => (
              <Col>
                <FormField
                  control={control}
                  name={`caption.${lang}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          label={t("attachment.form.caption")}
                          id={field.name}
                          type="text"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`textAlt.${lang}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          label={t("attachment.form.textAlt")}
                          id={field.name}
                          type="text"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </Col>
            )}
          />
          <FormField
            control={control}
            name={"position"}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      if (isMainPosition(value)) {
                        onFacePositionTriggered();
                      }
                    }}
                    value={field.value}
                  >
                    <Label
                      htmlFor={field.name}
                      className="flex text-start mb-1.5 text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                    >
                      {t("attachment.form.position")}
                      <div className="text-red-500">*</div>
                    </Label>
                    <SelectTrigger className="border-red-500">
                      <SelectValue
                        placeholder={t("attachment.form.position.placeholder")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {optionsPosition[type].map((position: string) => (
                        <SelectItem key={position} value={position}>{t(position)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={"order"}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value?.toString()}
                  >
                    <Label
                      htmlFor={field.name}
                      className="flex text-start mb-1.5 text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                    >
                      {t("attachment.form.order")}
                    </Label>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("attachment.form.order.placeholder")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4].map((position) => (
                        <SelectItem key={position} value={position.toString()}>
                          {position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={"published"}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    onValueChange={(v) => field.onChange(v === "true")}
                    value={field.value === true ? "true" : "false"}
                  >
                    <Label
                      htmlFor={field.name}
                      className="flex text-start mb-1.5 text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                    >
                      {t("imageFieldPublishedLabel")}
                    </Label>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("imageFieldPublishedPlaceholder")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">
                        {t("imageFieldPublished")}
                      </SelectItem>
                      <SelectItem value="false">
                        {t("imageFieldNotPublished")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />
          <Button type="submit" loading={loading} disabled={!form.formState.isValid || (form.getValues("image") === undefined && !selectedImage)} className={(form.getValues("image") === undefined && !selectedImage) ? 'bg-gray-300 text-gray-700' : ''}>
            {selectedImage ? t("ajoutImageBoutonEnregistrer") : t("uploadSaveImage")}
          </Button>
        </form>
      </FormProvider>
      <ShowOptionsDialog open={showDialog} onOpenChange={(open) => setShowDialog(open)} forfait={forfait ?? {} as Forfait} setSelectedOptionName={subscribeToOptionSuccess} />
      <Dialog open={openConfirmation} onOpenChange={setOpenConfirmation}>
        <DialogContent className="p-6">
          <div className="flex items-center justify-center my-4">
            <CheckCircleIcon className="w-8 h-8 text-primary" />
          </div>
          <div className="text-center text-2xl mx-4">
            {t("optionAjoutSuccesTitre1")} <strong>"{selectedOptionName}"</strong> {t("optionAjoutSuccesTitre2")}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <DialogContent className="max-w-md">
          <div className="text-center text-lg font-bold">
            {t("warningHdDialogDescription")}
          </div>
          <DialogClose>
            <div className="flex justify-between gap-2">
              <Button variant="outline" className="w-full" onClick={() => setShowWarningDialog(false)}>
                {t("formCancel")}
              </Button>
              <Button className="w-full" onClick={() => {
                setShowWarningDialog(false);
                form.handleSubmit((data) => {
                  data.isHd = hdOptionSelected && hasHdOption;
                  setHdOptionSelected(true);
                  onSubmit(data);
                }, (errors) => {
                  console.log(errors);
                })();
              }}>
                {t("confirmAddAttachmentDialog")}
              </Button>
            </div>
          </DialogClose>
        </DialogContent>
      </Dialog>
      <Dialog open={openConfirmationDelete} onOpenChange={setOpenConfirmationDelete}>
        <DialogContent className="p-6">
          <div className="flex items-center justify-center my-4">
            {t("imageSuppressionQuestion")}
          </div>
          <DialogClose>
            <div className="flex justify-between gap-2">
              <Button variant="outline" className="w-full" onClick={() => setOpenConfirmationDelete(false)}>
                {t("formCancel")}
              </Button>
              <Button className="w-full" onClick={() => {
                setOpenConfirmationDelete(false);
                onDelete?.(selectedImage?.id ?? "");
                toast({
                  title: t("toastImageSuppressionSuccesTitre"),
                  variant: "success",
                  description: t("toastImageSuppressionSuccesDescription"),
                });
                setOpen(false);
              }}>
                {t("confirmAddAttachmentDialog")}
              </Button>
            </div>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </StyledDrawer>
  );
};

interface MediaLibraryProps {
  id: string;
  type: TypeOptionPosition;
  invalidateOnChange?: () => void;
  computeImageName?: (data?: ImageFormData) => string;
  embedAttachmentOnSubmit?: boolean;
  setImageMode: (imageMode: boolean) => void;
}

const MediaLibrary = ({
  id,
  type,
  invalidateOnChange,
  computeImageName,
  embedAttachmentOnSubmit = false,
  setImageMode,
}: MediaLibraryProps) => {
  const { instanceData } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<Attachment | null>(null);

  // HD logic
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorContent, setErrorContent] = useState<string | null>(null);

  const { t } = useTranslation();

  const columns: ColumnDef<Attachment>[] = [
    {
      accessorKey: "publicUrl",
      header: t("gestionImagesTableImage"),
      cell: ({ row }) => (
        <img src={row.original.publicUrl} alt="image" width={50} />
      ),
    },
    // {
    //   accessorKey: "name",
    //   header: t("gestionImagesTableNom"),
    // },
    {
      cell: ({ row }) => t(row.original.position),
      header: t("gestionImagesTablePosition"),
    },
    {
      cell: ({ row }) =>
        row.original.published
          ? t("publishedFrontPublic")
          : t("imageFieldNotPublished"),
      header: t("gestionImagesTablePublished"),
    },
  ];

  const {
    data: attachments,
    isLoading,
    refetch,
  } = useGetAttachmentsByDocId(id);

  const { mutate: updateAttachment } = useUpdateAttachment();
  const { mutate: deleteAttachment } = useDeleteAttachment();
  const { mutateAsync: embedAttachment } = useEmbedAttachment();
  const { mutateAsync: deleteHdAttachment } = useDeleteHdAttachment();

  const form = useForm<ImageFormData>({
    resolver: zodResolver(imageSchema),
    reValidateMode: "onChange",
  });

  const UploadImage = async (data: ImageFormData) => {
    if (data.image) {
      if (instanceData == null) {
        console.error("Tried to upload an image, but instanceData is null");
        return;
      }

      if (selectedImage && !data.isHd && selectedImage.isHd) {
        deleteHdAttachment(selectedImage?.id ?? "", {
          onSuccess: () => {
            invalidateOnChange?.();
          },
        });
      }

      try {
        const attachment = await uploadImage(
          data.image,
          {
            ...data,
            published: data.published,
            position: data.position,
            docId: id,
            details: "",
            name: computeImageName?.(data) ?? "",
            isHd: data.isHd,
          } as Attachment,
          instanceData.s3Bucket,
          instanceData.id,
          "back/api/attachment/create",
          data.name,
        )
        toast({
          variant: "success",
          description: t("fileUploadSuccess"),
        });

        if (embedAttachmentOnSubmit) {
          embedAttachment(attachment.id);
        }
        refetch();
        invalidateOnChange?.();
        setOpen(false);
      } catch (e) {
        if (isAxiosError(e)) {
          setOpenErrorDialog(true);
          setErrorContent(e.response?.data.error);
          return;
        }
        form.setError("root", { message: t("unknownError") });
        throw e;
      }
    }
  };

  const updateImage = async (attachment: Attachment) => {
    setSelectedImage(attachment);
    form.reset({
      ...attachment,
    });
    setOpen(true);
  };

  const deleteImage = async () => {
    if (imageToDelete) {
      deleteAttachment(imageToDelete, {
        onSuccess: () => {
          toast({
            title: t("toastImageSuppressionSuccesTitre"),
            variant: "success",
            description: t("toastImageSuppressionSuccesDescription"),
          });
          invalidateOnChange?.();
        },
      });
    }
  };


  const handleImageEdit = async (data: ImageFormData) => {
    if (selectedImage) {
      try {
        await updateAttachment({
          id: selectedImage.id ?? "",
          attachment: {
            ...data,
            name: data.name,
            docId: id,
            details: "",
            published: data.published,
          } as Attachment,
        });
        setTimeout(() => {
          if (embedAttachmentOnSubmit) {
            embedAttachment(selectedImage.id);
          }
          toast({
            title: t("toastImageMAJSuccesTitre"),
            variant: "success",
            description: t("toastImageMAJSuccesDescription"),
          });
          refetch();
          invalidateOnChange?.();
          setOpen(false);
        }, 1000);
      } catch (e) {
        console.error(e);
        if (isAxiosError(e)) {
          setOpenErrorDialog(true);
          setErrorContent(e.response?.data.error);
          return;
        }
        toast({
          title: t("toastImageMAJErreurTitre"),
          variant: "error",
          description: t("toastImageMAJErreurDescription"),
        });
      }
    }
  };

  return (
    <Tabs defaultValue={"grid"}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChevronsLeft
            size={35}
            color="black"
            onClick={() => {
              setImageMode(false);
            }}
            className="cursor-pointer"
          />
          <h1 className="text-[32px] font-semibold">
            {t("titreGestionImages")}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="grid">
              <LayoutGrid className="h-4 w-4" />
            </TabsTrigger>
            <TabsTrigger value="list">
              <Table2 className="h-4 w-4" />
            </TabsTrigger>
          </TabsList>
          <UploadOrEditPicture
            form={form}
            open={open}
            setOpen={setOpen}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            onSubmit={selectedImage ? handleImageEdit : UploadImage}
            onDelete={
              selectedImage
                ? (id) =>
                  deleteAttachment(id, {
                    onSuccess: () => invalidateOnChange?.(),
                  })
                : undefined
            }
            type={type}
          />
        </div>
      </div>
      <Separator className="my-4" />
      <TabsContent value="grid">
        <div className="flex gap-2 flex-wrap">
          {isLoading && <p>{t("chargement")}</p>}
          {!isLoading &&
            attachments?.map((attachment: Attachment) => (
              <div key={attachment.id}>
                <div
                  className="w-[200px] h-[200px] bg-gray-100 rounded-lg cursor-pointer relative"
                >
                  {attachment.mediumPublicUrl && (
                    <UniqueImageFullScreen
                      media={{
                        url: attachment.mediumPublicUrl,
                        largeUrl:
                          attachment.largePublicUrl ??
                          attachment.mediumPublicUrl,
                        alt: "",
                        className: "w-full h-[200px] object-contain",
                      }}
                    />
                  )}
                  <Button
                    variant="white"
                    className="absolute bottom-1 right-1 text-gray-950 text-sm flex gap-2"
                    onClick={() => updateImage(attachment)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  {attachment.isHd && (
                    <Crown className="w-5 h-5 text-[#E2CDA4] absolute top-3 right-3" />
                  )}
                  <Button
                    className="absolute bottom-1 left-1 text-gray-950 text-sm flex gap-2"
                    variant="outline"
                    onClick={() => {
                      setImageToDelete(attachment.id);
                      setOpenConfirmation(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-[#DC2626]" />
                  </Button>
                </div>
                <p className="text-sm text-gray-500 w-[200px]">{attachment.name}</p>
                <p className="text-sm text-gray-500 mt-2">
                  {t(attachment.position)}
                </p>
                {attachment.published && (
                  <p className="text-sm text-green-500">
                    {t("publishedFrontPublic")}
                  </p>
                )}
              </div>
            ))}
        </div>
      </TabsContent>
      <TabsContent value="list">
        <DataTable
          onRowClick={(row) => {
            updateImage(row.original);
          }}
          columns={columns}
          data={attachments ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      </TabsContent>
      <Dialog open={openConfirmation} onOpenChange={setOpenConfirmation}>
        <DialogContent className="p-6">
          <div className="flex items-center justify-center my-4">
            {t("imageSuppressionQuestion")}
          </div>
          <DialogClose>
            <div className="flex justify-between gap-2">
              <Button variant="outline" className="w-full" onClick={() => setOpenConfirmation(false)}>
                {t("formCancel")}
              </Button>
              <Button className="w-full" onClick={() => {
                setOpenConfirmation(false);
                deleteImage();
              }}>
                {t("confirmAddAttachmentDialog")}
              </Button>
            </div>
          </DialogClose>
        </DialogContent>
      </Dialog>
      <Dialog open={openErrorDialog} onOpenChange={setOpenErrorDialog}>
        <DialogContent className="p-6">
          <VisuallyHidden>
            <DialogTitle className="sr-only">Error</DialogTitle>
          </VisuallyHidden>
          <div className="flex items-center justify-center my-4">
            <XCircleIcon className="w-8 h-8 text-red-500" />
          </div>
          <div className="text-center text-2xl mx-4">
            {t(errorContent ?? "")}
          </div>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
};

export default MediaLibrary;
